<script lang="ts" setup>
import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';
import { useFeedbackStore } from '~/stores/feedback';
import { useProductFeedback } from '~/composables/useProductReviews';

const reviewsPage = ref<number>(1);
const isReviewsLoading = ref(false);
const feedbackStore = useFeedbackStore();
const { reviews, reviewsPagination, averageRating } = storeToRefs(feedbackStore);
const { selectedOffer } = storeToRefs(useCatalogStore());
const { isLogged } = storeToRefs(useAuthStore());
const { isMobile } = useBreakpoints();
const { getReviewsCountByRating } = useProductFeedback();

const route = useRoute();
const isShowReplyModal = ref(false);

await feedbackStore.getReviews(selectedOffer.value.product_id);

async function loadMoreReviews() {
  isReviewsLoading.value = true;
  await feedbackStore.loadMoreReviews(selectedOffer.value.product_id, ++reviewsPage.value);
  isReviewsLoading.value = false;
}

onMounted(async () => {
  if (isLogged.value && route.query.reply === 'true') {
    isShowReplyModal.value = true;
  }
});

async function handleReplyBtnClick() {
  if (isLogged.value) {
    isShowReplyModal.value = true;
  } else {
    await navigateTo({
      path: route.path,
      hash: '#reviews',
      query: {
        ...route.query,
        reply: 'true',
      },
    });

    localStorage.setItem('return-url', route.fullPath);
    const toast = useToast();
    toast.info({ component: ToastComponent, props: { text: 'Чтобы оставить отзыв, нужно войти' } }, { timeout: 4000 });
    await navigateTo('/login', { external: true });
  }
}
</script>

<template>
  <div class="pt-4 text-sm">
    <div class="flex flex-col gap-4">
      <template v-if="reviews.length">
        <div class="flex items-center gap-5 flex-wrap">
          <RatingStars
            :rating="Math.round(Number(averageRating))"
            size="6"
          />

          <div class="text-xs sm:text-base flex-shrink-0">
            {{ Number(averageRating).toFixed(1) }} баллов на основе {{ reviewsPagination?.total ?? 0 }} оценок
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <div
            v-for="n in [5, 4, 3, 2, 1]"
            :key="n"
            class="grid grid-cols-[minmax(80px,1fr)_20fr_1fr] items-center gap-4"
          >
            <div class="flex-shrink-0">{{ n }} {{ getNoun(n, 'балл', 'балла', 'баллов') }}</div>

            <div class="flex flex-grow">
              <div
                :style="{ width: `${(getReviewsCountByRating(n) * 100) / reviews.length}%` }"
                class="bg-hell-black-60 h-1 rounded"
              />

              <div
                :style="{ width: `${100 - (getReviewsCountByRating(n) * 100) / reviews.length}%` }"
                class="bg-hell-black-20 h-1 rounded"
              />
            </div>

            <div class="text-end w-6">{{ getReviewsCountByRating(n) }}</div>
          </div>
        </div>
      </template>

      <div v-else>Пока нет отзывов</div>

      <TheBtn
        :size="isMobile ? 'sm' : 'lg'"
        class="w-full sm:w-fit"
        color="outline"
        @click="handleReplyBtnClick"
      >
        Оставить свой отзыв
      </TheBtn>
    </div>

    <div class="flex flex-col divide-y divide-solid divide-hell-black-20">
      <ProductPageReviewsListItem
        v-for="review in reviews"
        :key="review.id"
        :review
      />
    </div>

    <div class="flex justify-center">
      <TheBtn
        v-if="reviewsPagination && reviews.length < reviewsPagination.total"
        :is-loading="isReviewsLoading"
        class="w-fit mt-4"
        @click="loadMoreReviews"
      >
        Показать еще
      </TheBtn>
    </div>

    <ClientOnly>
      <ProductPageAddReviewModal v-model="isShowReplyModal" />
    </ClientOnly>
  </div>
</template>
